import { useAuth } from "@auth/AuthContext"
import { LanguageSwitcher } from "@components/LanguageSwitcher"
import { SchoolMark } from "@components/SchoolMark"
import { logger } from "@lib/logger"
import { isAxiosError } from "axios"
import { AlertCircle, ChevronRight, Eye, EyeOff, Loader2, Lock, Mail, Sparkles } from "lucide-react"
import { lazy, Suspense, useState, type FormEvent } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"

import {
  LOGIN_FAILED_ATTEMPTS_KEY,
  LOGIN_RESET_HINT_AFTER_ATTEMPTS,
  SESSION_EXPIRED_PARAM,
  SESSION_EXPIRED_VALUE,
} from "@/constants/auth"

// Lazy so the public password-reset surface (its JWT-free axios client + the
// reset hooks) stays out of the eagerly-loaded login chunk until the
// professor actually opens the forgot-password form.
const ForgotPasswordForm = lazy(() =>
  import("@features/account-activation").then((m) => ({ default: m.ForgotPasswordForm })),
)

/**
 * PRD-34 FR-19 — one message per cause. Until 2026-10 every failure, from a
 * dead Wi-Fi to the rate limit, read "Credenciales incorrectas", which sent
 * professors to reset a password that was fine.
 */
type LoginError =
  | { kind: "invalid_credentials" }
  | { kind: "rate_limited"; minutes: number | null; accountScope: boolean }
  | { kind: "offline" }
  | { kind: "server" }

function classifyLoginError(err: unknown): LoginError {
  if (!isAxiosError(err)) return { kind: "server" }
  if (!err.response) return { kind: "offline" }
  if (err.response.status === 401) return { kind: "invalid_credentials" }
  if (err.response.status === 429) {
    // Cast: the 429 body is `{detail, retry_after_seconds, scope}` (api/auth.py::login).
    const body = err.response.data as
      { retry_after_seconds?: number; scope?: "ip" | "account" } | undefined
    const seconds = body?.retry_after_seconds
    return {
      kind: "rate_limited",
      minutes: seconds ? Math.ceil(seconds / 60) : null,
      // "ip" = the school's Wi-Fi, not her password: no reset offer then.
      accountScope: body?.scope === "account",
    }
  }
  return { kind: "server" }
}

// sessionStorage can throw (private mode, blocked storage); the count is a
// hint, so losing it is fine.
function readFailedAttempts(): number {
  try {
    return Number(sessionStorage.getItem(LOGIN_FAILED_ATTEMPTS_KEY)) || 0
  } catch {
    return 0
  }
}

function writeFailedAttempts(count: number | null) {
  try {
    if (count === null) sessionStorage.removeItem(LOGIN_FAILED_ATTEMPTS_KEY)
    else sessionStorage.setItem(LOGIN_FAILED_ATTEMPTS_KEY, String(count))
  } catch {
    // Same reason as readFailedAttempts.
  }
}

export function LoginPage() {
  const { t } = useTranslation(["auth", "common"])
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<LoginError | null>(null)
  const [failedAttempts, setFailedAttempts] = useState(readFailedAttempts)
  const [showForgot, setShowForgot] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [searchParams] = useSearchParams()
  // Shown until the first attempt: after that the form's own state speaks,
  // and the notice reappearing on every retry would shove the form down.
  const sessionExpired =
    !submitted && searchParams.get(SESSION_EXPIRED_PARAM) === SESSION_EXPIRED_VALUE

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
    setLoading(true)
    setError(null)
    try {
      await login(email, password)
      writeFailedAttempts(null)
      navigate("/")
    } catch (err) {
      const failure = classifyLoginError(err)
      const status = isAxiosError(err) ? err.response?.status : undefined
      logger.warning("auth.login_failed", { status, code: failure.kind })
      if (failure.kind === "invalid_credentials") {
        const next = failedAttempts + 1
        setFailedAttempts(next)
        writeFailedAttempts(next)
      }
      setError(failure)
    } finally {
      setLoading(false)
    }
  }

  // PRD-34 FR-20 — the third wrong password, or the rate limit, points at
  // the reset form instead of inviting a fourth guess.
  const offerReset =
    !showForgot &&
    ((error?.kind === "rate_limited" && error.accountScope) ||
      (error?.kind === "invalid_credentials" && failedAttempts >= LOGIN_RESET_HINT_AFTER_ATTEMPTS))

  const errorMessage = (failure: LoginError): string => {
    switch (failure.kind) {
      case "invalid_credentials":
        return t("login.error.invalid_credentials")
      case "rate_limited":
        return failure.minutes
          ? t("login.error.rate_limited", { count: failure.minutes })
          : t("login.error.rate_limited_generic")
      case "offline":
        return t("login.error.offline")
      case "server":
        return t("login.error.server")
    }
  }

  // Three things the product does today (copy-follows-capability): the old
  // third line advertised the progress sheet PRD-58 retired.
  const features = [
    t("login.features.materials"),
    t("login.features.results"),
    t("login.features.scan"),
  ]

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* The marketing aside, on the ink tokens rather than a hex: near-black
          on a light device, near-white on a dark one, so the two halves
          contrast instead of showing two unrelated darks side by side. */}
      <aside className="bg-ink text-ink-inverted relative hidden flex-col justify-between p-10 lg:flex xl:p-14">
        <div className="flex items-center gap-2">
          {/* The same glyph SchoolMark draws in the app chrome: one mark. */}
          <Sparkles className="h-5 w-5" aria-hidden />
          <span className="text-base font-medium tracking-tight">{t("common:app.name")}</span>
        </div>

        <div className="max-w-xl">
          {/* A tagline, not the page's heading — the h1 is the form's, on
              every width; this panel is hidden below lg. */}
          <p className="text-4xl leading-tight font-bold tracking-tight xl:text-5xl xl:leading-[1.1]">
            {t("login.heading")}
          </p>
          <p className="text-ink-inverted/85 mt-5 text-base font-medium xl:text-lg">
            {t("login.subheading")}
          </p>

          <ul className="mt-10 space-y-4">
            {features.map((feature) => (
              <li key={feature} className="flex items-center gap-3">
                <span className="bg-ink-inverted/10 ring-ink-inverted/10 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1">
                  <ChevronRight className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                </span>
                <span className="text-sm xl:text-base">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-ink-inverted/60 text-xs">{t("common:app.cobrand_subtitle")}</p>
      </aside>

      <main className="bg-surface relative flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <SchoolMark
              fallbackName={t("common:app.name")}
              subtitle={t("common:app.cobrand_subtitle")}
            />
          </div>

          {/* Its own words: the heading used to reuse the button's label. */}
          <h1 className="text-ink text-3xl font-bold tracking-tight">{t("login.title")}</h1>
          <p className="text-ink-subtle mt-2 text-sm">{t("login.subtitle")}</p>

          {sessionExpired && (
            <p
              role="status"
              className="rounded-control border-accent bg-accent-soft text-accent-ink mt-6 border px-3 py-2 text-sm"
            >
              {t("login.session_expired")}
            </p>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="email" className="text-ink mb-1.5 block text-sm font-medium">
                {t("login.email.label")}
              </label>
              <div className="relative">
                <Mail
                  className="text-ink-subtle pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                  aria-hidden="true"
                />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.email.placeholder")}
                  className="rounded-control bg-inset text-ink placeholder:text-ink-subtle focus:border-border focus:bg-surface min-h-11 w-full border border-transparent pr-3 pl-10 text-base md:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="text-ink mb-1.5 block text-sm font-medium">
                {t("login.password.label")}
              </label>
              <div className="relative">
                <Lock
                  className="text-ink-subtle pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                  aria-hidden="true"
                />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-control bg-inset text-ink placeholder:text-ink-subtle focus:border-border focus:bg-surface min-h-11 w-full border border-transparent pr-10 pl-10 text-base md:text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t("login.password.hide") : t("login.password.show")}
                  className="rounded-control text-ink-subtle hover:bg-inset hover:text-ink absolute top-1/2 right-1 flex h-11 w-11 -translate-y-1/2 items-center justify-center md:h-8 md:w-8"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-control border-danger bg-danger-soft text-danger-ink flex items-start gap-2 border px-3 py-2 text-sm"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div className="space-y-1">
                  <p>{errorMessage(error)}</p>
                  {offerReset && (
                    <>
                      <p>{t("login.error.reset_hint")}</p>
                      <button
                        type="button"
                        onClick={() => setShowForgot(true)}
                        className="text-danger-ink inline-flex min-h-11 items-center font-semibold underline underline-offset-2 md:min-h-8"
                      >
                        {t("login.error.reset_cta")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="rounded-control bg-accent hover:bg-accent-hover text-ink-inverted flex min-h-11 w-full items-center justify-center gap-2 px-4 text-sm font-semibold transition-colors duration-150 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? t("login.submitting") : t("login.submit")}
            </button>
          </form>

          <div className="border-hairline mt-6 border-t pt-4">
            {showForgot ? (
              <Suspense
                fallback={
                  <div className="flex justify-center py-2">
                    <Loader2 className="text-ink-subtle h-4 w-4 animate-spin" aria-hidden />
                  </div>
                }
              >
                <ForgotPasswordForm onCancel={() => setShowForgot(false)} initialEmail={email} />
              </Suspense>
            ) : (
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="rounded-control text-ink-muted hover:text-ink inline-flex min-h-11 items-center px-2 text-sm font-medium md:min-h-8"
              >
                {t("forgot.trigger")}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
